import { Color } from "utils/theme";
import { RequestTopics } from "services/topics";
import { DataStatus } from "screens/settings/components/data-status";
import useMqttApi from "hooks/use-mqtt-api";
import { useStore } from "hooks/use-store";
import { toast } from "react-toastify";
import { SystemStatus as ISystemStatus } from "types/system-status";
import { useTranslation } from "react-i18next";
import { isCanvasSupported } from "utils/canvas";
import {
  getBrowserMemoryUsage,
  getDevicePixelRatio,
  getWindowSize,
} from "../utils";
import { StatusSection } from "../components/status-section";
import { TabContainer } from "../components/tab-container";
import { ScrollButtons } from "components/ui/scroll-buttons";
import { useRef } from "react";
import { Spinner } from "components/ui/spinner";
import { ErrorState } from "../components/error-state";

import packageJson from "../../../../package.json";

export const SystemInformation = ({ title }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "settings.system_status",
  });
  const { t: tCommon } = useTranslation("translation", { keyPrefix: "common" });

  const config = useStore((state) => state.config);
  const browserSpeed = useStore((state) => state.browserSpeed);

  const {
    data: status,
    loading,
    error,
    fetch,
  } = useMqttApi<{ data: ISystemStatus }>({
    requestTopic: RequestTopics.SystemStatus,
    enabled: true,
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const ref = useRef<HTMLDivElement>(null);

  if (error) {
    return (
      <ErrorState
        title={t("error.title")}
        description={t("error.description")}
        detail={error.message}
        retryLabel={t("error.retry")}
        retrying={loading}
        onRetry={() => fetch()}
      />
    );
  }

  if (loading) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="w-full flex-1 flex items-center justify-center">
        <p className="p-5" style={{ color: Color.Primary[400] }}>
          {t("no_data_message")}
        </p>
      </div>
    );
  }

  return (
    <TabContainer ref={ref} title={title}>
      <ScrollButtons containerRef={ref} className="absolute bottom-4 right-4" />
      <StatusSection
        title={"Helm Software"}
        description={
          "Information about the Helm software running on this device."
        }
      >
        <DataStatus label={"Software Version"} value={packageJson.version} />
      </StatusSection>
      <StatusSection
        title={t("config.title")}
        description={t("config.description")}
      >
        <DataStatus label={"Device ID"} value={config?.device_id} />
        <DataStatus label={t("config.paired_vessel")} value={config?.vessel} />
        <DataStatus
          label={t("config.captain_required")}
          value={
            config?.company_settings?.force_captain_login
              ? tCommon("required")
              : tCommon("optional")
          }
        />
      </StatusSection>
      <StatusSection
        title={t("browser.title")}
        description={t("browser.description")}
      >
        <DataStatus
          label={t("browser.user_agent")}
          value={`${window.navigator.userAgent}`}
        />
        <DataStatus
          label={t("browser.canvas_support")}
          value={
            isCanvasSupported()
              ? t("browser.supported")
              : t("browser.unsupported")
          }
        />
        <DataStatus
          label={t("browser.resolution")}
          value={`${getWindowSize()} ${getDevicePixelRatio()}`}
        />
        <DataStatus label={t("browser.opsPerMs")} value={browserSpeed} />
        <DataStatus
          label={t("browser.memory_usage")}
          value={getBrowserMemoryUsage()}
        />
      </StatusSection>
    </TabContainer>
  );
};
